import type {
  AbrirSemana,
  ActualizarCausa,
  ActualizarNoConformidad,
  ActualizarUsuario,
  AmbitoFicha,
  AmbitoPesos,
  AsignarFotos,
  AutorFotos,
  AvanceSprint,
  BcPreview,
  BcReporteRespuesta,
  BcResumen,
  CategoriaCalidad,
  Causa,
  CierrePreview,
  ConfigFotos,
  ConfirmarFoto,
  ControlesObra,
  CrearCausa,
  CrearRegistro,
  CrearUsuario,
  EjecutarCorteA,
  EstadoObra,
  EstadoVenta,
  FichaDetalle,
  FichaListado,
  FijarLineaBaseResultado,
  GuardarFicha,
  GuardarHoras,
  GuardarPesos,
  GuardarRespuestas,
  GuardarTipoCasaSprints,
  HistoricoReporte,
  HitoDesembolsoCodigo,
  HorasObra,
  IniciarObra,
  IniciarObraLote,
  M2Reporte,
  ManoObraAcumuladoReporte,
  MatrizAvance,
  MatrizPesos,
  MesAvanceReporte,
  NoConformidad,
  NominaSemanal,
  Obra,
  ObraAvanceReporte,
  ObraFotos,
  PaginaFotos,
  PendientesReporte,
  PresupuestoDetalle,
  PresupuestoResumen,
  Proyecto,
  PuntoCatalogo,
  RegistrarAvance,
  RegistroDetalle,
  RegistroListado,
  ReporteSemanal,
  ResetPin,
  RespuestaApi,
  ResultadoCierre,
  ResultadoCorteA,
  ResultadoFotoB,
  ResultadoIniciarLote,
  RevertirAvancePreview,
  RevertirAvanceResultado,
  SemanaOperativa,
  SprintCatalogo,
  SubPartidaCrear,
  SubPartidaDetalle,
  SubPartidaListado,
  SubPartidaPatch,
  Subcontrato,
  SubidaFoto,
  TipoCasa,
  TipoCasaSprints,
  TipoCentroCosto,
  TipoFicha,
  UsuarioApp,
} from '@adelante/shared';

/** Estado operativo liviano (GET /api/obra-estados) — sin datos de vw_obras. */
export interface ObraEstadoLite {
  codigo: string;
  estado: EstadoObra;
  tipo_casa: TipoCasa | null;
  sprint_actual: number;
  estado_venta: EstadoVenta | null;
}

/** Partida con info del grupo (devuelto por GET /api/partidas). */
export interface PartidaListado {
  id: number;
  codigo: string;
  nombre: string;
  grupo_id: number;
  grupo_codigo: string;
  grupo_nombre: string;
  orden: number | null;
  activo: boolean;
}
import {
  type UsuarioSesion,
  cerrarSesion,
  modoDev,
  obtenerIniciales,
  obtenerToken,
  obtenerUsuarioGuardado,
  usuarioDev,
} from './auth';

const API_URL = import.meta.env.VITE_API_URL;

/** Error de respuesta HTTP con el código de estado para que los hooks lo inspeccionen. */
export class ErrorHttp extends Error {
  constructor(
    readonly status: number,
    mensaje: string,
  ) {
    super(mensaje);
    this.name = 'ErrorHttp';
  }
}

/**
 * Wrapper genérico sobre `fetch` que añade auth y maneja respuestas RespuestaApi<T>.
 * Si el backend devuelve 401, limpia la sesión y redirige a /login.
 */
async function pedir<T>(ruta: string, opciones?: RequestInit): Promise<T> {
  const token = obtenerToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((opciones?.headers ?? {}) as Record<string, string>),
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const respuesta = await fetch(`${API_URL}${ruta}`, { ...opciones, headers });

  if (respuesta.status === 401 && !modoDev) {
    cerrarSesion();
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
      window.location.assign('/login');
    }
    throw new Error('Sesión expirada — redirigiendo a login');
  }

  if (!respuesta.ok) {
    // Intentar leer el mensaje real del backend (RespuestaApi.error) para que el
    // toast muestre la causa y no un genérico "HTTP 500".
    let detalle = `HTTP ${respuesta.status}: ${respuesta.statusText}`;
    try {
      const cuerpo = (await respuesta.json()) as { error?: unknown };
      const err = cuerpo?.error as { mensaje?: string; message?: string } | string | undefined;
      const msg = typeof err === 'string' ? err : (err?.mensaje ?? err?.message);
      if (msg) detalle = msg;
    } catch {
      /* respuesta sin cuerpo JSON */
    }
    throw new ErrorHttp(respuesta.status, detalle);
  }

  return (await respuesta.json()) as T;
}

/** Filtros de la galería de fotos de una obra. */
export interface FiltroGaleria {
  /** Sin obra = galería general de todas las obras. */
  obra?: string | null;
  /** Prefijo del proyecto (ej. VN, INF). */
  proyecto?: string | null;
  /** 'suelta' = fotos de obra; 'ficha' = de fichas de control / NC. */
  origen?: 'suelta' | 'ficha' | null;
  /** Tipo de centro de costo (vivienda, infraestructura, …). */
  tipo?: TipoCentroCosto | null;
  sub?: number | null;
  partida?: number | null;
  sin_sub?: boolean;
  autor?: number | null;
  desde?: string | null;
  hasta?: string | null;
}

/**
 * Filtros aceptados por GET /api/obras.
 */
export interface ObrasFiltros {
  proyecto?: string;
  bloque?: string;
  tipo_casa?: TipoCasa;
  status?: string;
}

function construirQueryString(filtros: ObrasFiltros): string {
  const params = new URLSearchParams();
  if (filtros.proyecto) params.set('proyecto', filtros.proyecto);
  if (filtros.bloque) params.set('bloque', filtros.bloque);
  if (filtros.tipo_casa) params.set('tipo_casa', filtros.tipo_casa);
  if (filtros.status) params.set('status', filtros.status);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Cliente API tipado. Cada agrupación corresponde a un módulo del backend.
 * Para agregar un endpoint nuevo: extender este objeto y reutilizar `pedir<T>`.
 */
export const cliente = {
  auth: {
    me: () => pedir<RespuestaApi<UsuarioSesion>>('/api/auth/me'),
    cambiarPin: (pin_actual: string, pin_nuevo: string) =>
      pedir<RespuestaApi<{ ok: boolean }>>('/api/auth/cambiar-pin', {
        method: 'POST',
        body: JSON.stringify({ pin_actual, pin_nuevo }),
      }),
  },
  proyectos: {
    listar: (todos = false) =>
      pedir<RespuestaApi<Proyecto[]>>(`/api/proyectos${todos ? '?todos=true' : ''}`),
  },
  obras: {
    listar: (filtros: ObrasFiltros = {}) =>
      pedir<RespuestaApi<Obra[]>>(`/api/obras${construirQueryString(filtros)}`),
    /** Estado operativo liviano (sin vw_obras) — para el Kanban. */
    estados: () => pedir<RespuestaApi<ObraEstadoLite[]>>('/api/obra-estados'),
    obtener: (codigo: string) =>
      pedir<RespuestaApi<Obra>>(`/api/obras/${encodeURIComponent(codigo)}`),
    iniciar: (codigo: string, body: IniciarObra) =>
      pedir<RespuestaApi<{ obra_codigo: string; estado: string; sprint_actual: number }>>(
        `/api/obras/${encodeURIComponent(codigo)}/iniciar`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    iniciarLote: (body: IniciarObraLote) =>
      pedir<RespuestaApi<ResultadoIniciarLote>>('/api/obras/iniciar-lote', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    avance: (codigo: string, sprint?: number) =>
      pedir<RespuestaApi<AvanceSprint>>(
        `/api/obras/${encodeURIComponent(codigo)}/avance${sprint ? `?sprint=${sprint}` : ''}`,
      ),
    /** Avance completo de todas las obras (del proyecto) en un solo request. */
    avanceLote: (proyecto: string | null) =>
      pedir<RespuestaApi<Record<string, AvanceSprint>>>(
        `/api/avance-lote${proyecto ? `?proyecto=${encodeURIComponent(proyecto)}` : ''}`,
      ),
    /** Marca como completadas las arrastradas (sprints previos) de la obra. */
    completarArrastradas: (codigo: string) =>
      pedir<RespuestaApi<{ obra_codigo: string; completadas: number }>>(
        `/api/obras/${encodeURIComponent(codigo)}/completar-arrastradas`,
        { method: 'POST' },
      ),
    matriz: (proyecto: string | null, bloque?: string, semana?: number | null) => {
      const params = new URLSearchParams();
      if (proyecto) params.set('proyecto', proyecto);
      if (bloque) params.set('bloque', bloque);
      if (semana) params.set('semana', String(semana));
      const qs = params.toString();
      return pedir<RespuestaApi<MatrizAvance>>(`/api/obras/matriz${qs ? `?${qs}` : ''}`);
    },
    registrarAvance: (codigo: string, body: RegistrarAvance) =>
      pedir<RespuestaApi<{ obra_codigo: string; sub_partida_id: number; pct: number }>>(
        `/api/obras/${encodeURIComponent(codigo)}/avance`,
        { method: 'PUT', body: JSON.stringify(body) },
      ),
    avanzarSprint: (codigo: string) =>
      pedir<RespuestaApi<{ obra_codigo: string; sprint_de: number; sprint_a: number }>>(
        `/api/obras/${encodeURIComponent(codigo)}/sprint/avanzar`,
        { method: 'POST' },
      ),
    retrocederSprint: (codigo: string) =>
      pedir<RespuestaApi<{ obra_codigo: string; sprint_de: number; sprint_a: number }>>(
        `/api/obras/${encodeURIComponent(codigo)}/sprint/retroceder`,
        { method: 'POST' },
      ),
    setEstado: (codigo: string, estado: string, motivo?: string | null) =>
      pedir<RespuestaApi<{ obra_codigo: string; estado: string }>>(
        `/api/obras/${encodeURIComponent(codigo)}/estado`,
        { method: 'POST', body: JSON.stringify({ estado, motivo_inactiva: motivo ?? null }) },
      ),
    /** Fija el orden (prioridad) de las obras dentro de un sprint en el Kanban. */
    reordenar: (codigos: string[]) =>
      pedir<RespuestaApi<{ reordenadas: number }>>('/api/obras/reordenar', {
        method: 'PUT',
        body: JSON.stringify({ codigos }),
      }),
  },
  sprints: {
    /** Catálogo de sprints (nombre, es_espera, nº de críticas, hito de desembolso). */
    catalogo: () => pedir<RespuestaApi<SprintCatalogo[]>>('/api/sprints'),
    /** Marca/desmarca un sprint como "de espera" (colado/curado). */
    marcarEspera: (numero: number, es_espera: boolean) =>
      pedir<RespuestaApi<{ numero_global: number; es_espera: boolean }>>(`/api/sprints/${numero}`, {
        method: 'PATCH',
        body: JSON.stringify({ es_espera }),
      }),
    /** Asigna (o quita, con null) el hito de desembolso que dispara este sprint. */
    marcarHito: (numero: number, hito_desembolso: HitoDesembolsoCodigo | null) =>
      pedir<RespuestaApi<{ numero_global: number; hito_desembolso: HitoDesembolsoCodigo | null }>>(
        `/api/sprints/${numero}`,
        { method: 'PATCH', body: JSON.stringify({ hito_desembolso }) },
      ),
  },
  subPartidas: {
    listar: (filtros: SubPartidasFiltros = {}) =>
      pedir<RespuestaApi<SubPartidaListado[]>>(
        `/api/sub-partidas${construirQuerySubPartidas(filtros)}`,
      ),
    obtener: (id: number) => pedir<RespuestaApi<SubPartidaDetalle>>(`/api/sub-partidas/${id}`),
    crear: (body: SubPartidaCrear) =>
      pedir<RespuestaApi<SubPartidaDetalle>>('/api/sub-partidas', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    actualizar: (id: number, parche: SubPartidaPatch) =>
      pedir<RespuestaApi<SubPartidaDetalle>>(`/api/sub-partidas/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(parche),
      }),
  },
  partidas: {
    listar: () => pedir<RespuestaApi<PartidaListado[]>>('/api/partidas'),
  },
  pesos: {
    matriz: (ambito: AmbitoPesos) => pedir<RespuestaApi<MatrizPesos>>(`/api/pesos/${ambito}`),
    guardar: (ambito: AmbitoPesos, body: GuardarPesos) =>
      pedir<RespuestaApi<MatrizPesos>>(`/api/pesos/${ambito}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
  },
  parametros: {
    eficienciaCrono: () =>
      pedir<RespuestaApi<{ eficiencia: number }>>('/api/parametros/eficiencia-crono'),
    guardarEficienciaCrono: (eficiencia: number) =>
      pedir<RespuestaApi<{ eficiencia: number }>>('/api/parametros/eficiencia-crono', {
        method: 'PUT',
        body: JSON.stringify({ eficiencia }),
      }),
  },
  tiposCasa: {
    listar: () => pedir<RespuestaApi<TipoCasaSprints[]>>('/api/tipos-casa'),
    guardar: (tipo: string, body: GuardarTipoCasaSprints) =>
      pedir<RespuestaApi<TipoCasaSprints[]>>(`/api/tipos-casa/${encodeURIComponent(tipo)}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
  },
  usuarios: {
    listar: () => pedir<RespuestaApi<UsuarioApp[]>>('/api/usuarios'),
    crear: (body: CrearUsuario) =>
      pedir<RespuestaApi<{ id: number }>>('/api/usuarios', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    actualizar: (id: number, body: ActualizarUsuario) =>
      pedir<RespuestaApi<{ id: number }>>(`/api/usuarios/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    resetPin: (id: number, body: ResetPin) =>
      pedir<RespuestaApi<{ id: number }>>(`/api/usuarios/${id}/reset-pin`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    desbloquear: (id: number) =>
      pedir<RespuestaApi<{ id: number }>>(`/api/usuarios/${id}/desbloquear`, {
        method: 'POST',
      }),
  },
  semanas: {
    listar: () => pedir<RespuestaApi<SemanaOperativa[]>>('/api/semanas'),
    actual: () => pedir<RespuestaApi<SemanaOperativa | null>>('/api/semanas/actual'),
    abrir: (body: AbrirSemana) =>
      pedir<RespuestaApi<SemanaOperativa>>('/api/semanas/abrir', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    fijarLineaBase: (semanaId: number) =>
      pedir<RespuestaApi<FijarLineaBaseResultado>>(`/api/semanas/${semanaId}/linea-base`, {
        method: 'POST',
      }),
    revertirAvancePreview: (semanaId: number) =>
      pedir<RespuestaApi<RevertirAvancePreview>>(`/api/semanas/${semanaId}/revertir-avance`),
    revertirAvance: (semanaId: number) =>
      pedir<RespuestaApi<RevertirAvanceResultado>>(`/api/semanas/${semanaId}/revertir-avance`, {
        method: 'POST',
      }),
    actualizar: (
      semanaId: number,
      body: { dias_efectivos?: number; descripcion?: string | null },
    ) =>
      pedir<RespuestaApi<SemanaOperativa>>(`/api/semanas/${semanaId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
  },
  reportes: {
    semanal: (semanaId: number, venta: 'todas' | 'formalizadas' | 'no_formalizadas' = 'todas') =>
      pedir<RespuestaApi<ReporteSemanal>>(
        `/api/reportes/semanal?semana=${semanaId}&venta=${venta}`,
      ),
    historico: (semanaId: number) =>
      pedir<RespuestaApi<HistoricoReporte>>(`/api/reportes/historico?semana=${semanaId}`),
    m2: (semanaId: number) => pedir<RespuestaApi<M2Reporte>>(`/api/reportes/m2?semana=${semanaId}`),
    obraAvance: (semanaId: number, obra: string) =>
      pedir<RespuestaApi<ObraAvanceReporte>>(
        `/api/reportes/obra-avance?semana=${semanaId}&obra=${encodeURIComponent(obra)}`,
      ),
    mes: (mes: string) => pedir<RespuestaApi<MesAvanceReporte>>(`/api/reportes/mes?mes=${mes}`),
    pendientes: () => pedir<RespuestaApi<PendientesReporte>>('/api/reportes/pendientes'),
  },
  mo: {
    nomina: () => pedir<RespuestaApi<NominaSemanal[]>>('/api/mo/nomina'),
    guardarNomina: (
      body: Omit<NominaSemanal, 'anio' | 'numero_semana' | 'fecha_inicio' | 'fecha_fin'>,
    ) =>
      pedir<RespuestaApi<{ semana_operativa_id: number }>>('/api/mo/nomina', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    horas: () => pedir<RespuestaApi<HorasObra[]>>('/api/mo/horas'),
    guardarHoras: (body: GuardarHoras) =>
      pedir<RespuestaApi<{ semana_operativa_id: number; filas: number }>>('/api/mo/horas', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    subcontratos: () => pedir<RespuestaApi<Subcontrato[]>>('/api/mo/subcontratos'),
    crearSubcontrato: (body: Omit<Subcontrato, 'id'>) =>
      pedir<RespuestaApi<{ id: number }>>('/api/mo/subcontratos', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    eliminarSubcontrato: (id: number) =>
      pedir<RespuestaApi<{ id: number }>>(`/api/mo/subcontratos/${id}`, { method: 'DELETE' }),
    reporteAcumulado: () =>
      pedir<RespuestaApi<ManoObraAcumuladoReporte>>('/api/mo/reporte-acumulado'),
  },
  cierre: {
    preview: () => pedir<RespuestaApi<CierrePreview>>('/api/cierre/corte-a/preview'),
    corteA: (body: EjecutarCorteA) =>
      pedir<RespuestaApi<ResultadoCorteA>>('/api/cierre/corte-a', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    fotoB: (fecha_corte?: string) =>
      pedir<RespuestaApi<ResultadoFotoB>>('/api/cierre/foto-b', {
        method: 'POST',
        body: JSON.stringify({ fecha_corte }),
      }),
  },
  causas: {
    listar: () => pedir<RespuestaApi<Causa[]>>('/api/causas'),
    crear: (body: CrearCausa) =>
      pedir<RespuestaApi<{ id: number }>>('/api/causas', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    actualizar: (id: number, body: ActualizarCausa) =>
      pedir<RespuestaApi<{ id: number }>>(`/api/causas/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
  },
  presupuestos: {
    listar: (worksNo?: string) =>
      pedir<RespuestaApi<PresupuestoResumen[]>>(
        `/api/presupuestos${worksNo ? `?works_no=${encodeURIComponent(worksNo)}` : ''}`,
      ),
    detalle: (worksNo: string, version?: string) =>
      pedir<RespuestaApi<PresupuestoDetalle>>(
        `/api/presupuestos/${encodeURIComponent(worksNo)}${
          version ? `?version=${encodeURIComponent(version)}` : ''
        }`,
      ),
  },
  etl: {
    estado: () =>
      pedir<
        RespuestaApi<{
          estado: string;
          iniciado_en: string | null;
          finalizado_en: string | null;
          exit_code: number | null;
          detalle: string | null;
          ultima_carga_bd: string | null;
        }>
      >('/api/etl/estado'),
    ejecutar: () =>
      pedir<RespuestaApi<{ iniciado: boolean }>>('/api/etl/ejecutar', { method: 'POST' }),
  },
  bc: {
    resumen: () => pedir<RespuestaApi<BcResumen>>('/api/bc/resumen'),
    preview: (obra: string) =>
      pedir<RespuestaApi<BcPreview>>(`/api/bc/preview?obra=${encodeURIComponent(obra)}`),
    reportar: (obra: string, partidas?: string[]) =>
      pedir<RespuestaApi<BcReporteRespuesta>>('/api/bc/reportar', {
        method: 'POST',
        body: JSON.stringify({ obra, partidas }),
      }),
    registrar: (obra: string, fecha: string) =>
      pedir<RespuestaApi<{ obra: string; fecha: string; resultado: string }>>('/api/bc/registrar', {
        method: 'POST',
        body: JSON.stringify({ obra, fecha }),
      }),
    inicializar: (obra: string) =>
      pedir<RespuestaApi<{ obra: string; resultado: string }>>('/api/bc/inicializar', {
        method: 'POST',
        body: JSON.stringify({ obra }),
      }),
  },
  fotos: {
    /** Centros de costo de BC (todas las obras) con conteo de fotos. */
    obras: (q?: string) =>
      pedir<RespuestaApi<ObraFotos[]>>(`/api/fotos/obras${q ? `?q=${encodeURIComponent(q)}` : ''}`),
    /** Galería de una obra (más recientes primero; `antes` = cursor). */
    galeria: (f: FiltroGaleria & { antes?: string | null }) => {
      const p = new URLSearchParams();
      if (f.obra) p.set('obra', f.obra);
      if (f.proyecto) p.set('proyecto', f.proyecto);
      if (f.origen) p.set('origen', f.origen);
      if (f.tipo) p.set('tipo', f.tipo);
      if (f.sub) p.set('sub', String(f.sub));
      if (f.partida) p.set('partida', String(f.partida));
      if (f.sin_sub) p.set('sin_sub', 'true');
      if (f.autor) p.set('autor', String(f.autor));
      if (f.desde) p.set('desde', f.desde);
      if (f.hasta) p.set('hasta', f.hasta);
      if (f.antes) p.set('antes', f.antes);
      return pedir<RespuestaApi<PaginaFotos>>(`/api/fotos?${p.toString()}`);
    },
    /** Quiénes han subido fotos (filtro de autor). */
    autores: () => pedir<RespuestaApi<AutorFotos[]>>('/api/fotos/autores'),
    /** Configuración de centros de costo para fotos (admin). */
    config: () => pedir<RespuestaApi<ConfigFotos>>('/api/fotos/config'),
    guardarDias: (dias: number) =>
      pedir<RespuestaApi<{ dias: number }>>('/api/fotos/config/dias', {
        method: 'PUT',
        body: JSON.stringify({ dias }),
      }),
    guardarArea: (body: { area_costeo: string; tipo: TipoCentroCosto; admite_fotos: boolean }) =>
      pedir<RespuestaApi<unknown>>('/api/fotos/config/areas', {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    configurarObra: (
      codigo: string,
      body: { fotos_habilitada: boolean | null; nota?: string | null },
    ) =>
      pedir<RespuestaApi<unknown>>(`/api/fotos/config/obras/${encodeURIComponent(codigo)}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    asignar: (body: AsignarFotos) =>
      pedir<RespuestaApi<{ actualizadas: number; omitidas: number }>>('/api/fotos/asignar', {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
  },
  calidad: {
    categorias: () => pedir<RespuestaApi<CategoriaCalidad[]>>('/api/calidad/categorias'),
    fichas: (f: { tipo?: TipoFicha; ambito?: AmbitoFicha; activo?: boolean } = {}) => {
      const p = new URLSearchParams();
      if (f.tipo) p.set('tipo', f.tipo);
      if (f.ambito) p.set('ambito', f.ambito);
      if (f.activo) p.set('activo', 'true');
      const qs = p.toString();
      return pedir<RespuestaApi<FichaListado[]>>(`/api/calidad/fichas${qs ? `?${qs}` : ''}`);
    },
    ficha: (id: string) => pedir<RespuestaApi<FichaDetalle>>(`/api/calidad/fichas/${id}`),
    /** Todos los puntos activos de todas las fichas (búsqueda). */
    puntos: () => pedir<RespuestaApi<PuntoCatalogo[]>>('/api/calidad/puntos'),
    crearFicha: (body: GuardarFicha) =>
      pedir<RespuestaApi<{ id: string }>>('/api/calidad/fichas', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    guardarFicha: (id: string, body: GuardarFicha) =>
      pedir<RespuestaApi<{ id: string }>>(`/api/calidad/fichas/${id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    /** Solo fichas sin registros; con registros responde 409 (desactivarla). */
    eliminarFicha: (id: string) =>
      pedir<RespuestaApi<{ id: string }>>(`/api/calidad/fichas/${id}`, { method: 'DELETE' }),
    controlesObra: (codigo: string) =>
      pedir<RespuestaApi<ControlesObra>>(
        `/api/calidad/obras/${encodeURIComponent(codigo)}/controles`,
      ),
    registros: (f: { obra?: string; ficha?: string; estado?: string } = {}) => {
      const p = new URLSearchParams();
      if (f.obra) p.set('obra', f.obra);
      if (f.ficha) p.set('ficha', f.ficha);
      if (f.estado) p.set('estado', f.estado);
      const qs = p.toString();
      return pedir<RespuestaApi<RegistroListado[]>>(`/api/calidad/registros${qs ? `?${qs}` : ''}`);
    },
    registro: (id: string) => pedir<RespuestaApi<RegistroDetalle>>(`/api/calidad/registros/${id}`),
    iniciarRegistro: (body: CrearRegistro) =>
      pedir<RespuestaApi<{ id: string; existente: boolean }>>('/api/calidad/registros', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    guardarRespuestas: (id: string, body: GuardarRespuestas) =>
      pedir<RespuestaApi<{ id: string; guardadas: number }>>(
        `/api/calidad/registros/${id}/respuestas`,
        { method: 'PUT', body: JSON.stringify(body) },
      ),
    cerrarRegistro: (id: string, nota?: string | null) =>
      pedir<RespuestaApi<ResultadoCierre>>(`/api/calidad/registros/${id}/cerrar`, {
        method: 'POST',
        body: JSON.stringify({ nota: nota ?? null }),
      }),
    liberarRegistro: (id: string, justificacion: string) =>
      pedir<RespuestaApi<{ id: string; no_conformidades_creadas: number }>>(
        `/api/calidad/registros/${id}/liberar`,
        { method: 'POST', body: JSON.stringify({ justificacion }) },
      ),
    anularRegistro: (id: string, motivo: string) =>
      pedir<RespuestaApi<{ id: string }>>(`/api/calidad/registros/${id}/anular`, {
        method: 'POST',
        body: JSON.stringify({ motivo }),
      }),
    noConformidades: (f: { obra?: string; estado?: string } = {}) => {
      const p = new URLSearchParams();
      if (f.obra) p.set('obra', f.obra);
      if (f.estado) p.set('estado', f.estado);
      const qs = p.toString();
      return pedir<RespuestaApi<NoConformidad[]>>(
        `/api/calidad/no-conformidades${qs ? `?${qs}` : ''}`,
      );
    },
    actualizarNoConformidad: (id: string, body: ActualizarNoConformidad) =>
      pedir<RespuestaApi<{ id: string }>>(`/api/calidad/no-conformidades/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    pedirSubidaFoto: () =>
      pedir<RespuestaApi<SubidaFoto>>('/api/calidad/fotos/subida', { method: 'POST' }),
    confirmarFoto: (body: ConfirmarFoto) =>
      pedir<RespuestaApi<{ id: string }>>('/api/calidad/fotos', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    eliminarFoto: (id: string) =>
      pedir<RespuestaApi<{ id: string }>>(`/api/calidad/fotos/${id}`, { method: 'DELETE' }),
  },
};

export interface SubPartidasFiltros {
  partida_id?: number;
  sprint?: number;
  tipo_casa?: TipoCasa;
  activo?: boolean;
  q?: string;
}

function construirQuerySubPartidas(f: SubPartidasFiltros): string {
  const params = new URLSearchParams();
  if (f.partida_id !== undefined) params.set('partida_id', String(f.partida_id));
  if (f.sprint !== undefined) params.set('sprint', String(f.sprint));
  if (f.tipo_casa) params.set('tipo_casa', f.tipo_casa);
  if (f.activo !== undefined) params.set('activo', f.activo ? 'true' : 'false');
  if (f.q) params.set('q', f.q);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/** Información del usuario activo para la UI (header, avatar). */
export interface UsuarioActivo {
  name: string;
  username: string;
  iniciales: string;
  rol: string;
}

/**
 * Obtiene el usuario activo desde localStorage (o usuarioDev en modo dev).
 * Devuelve null si no hay sesión.
 */
export function obtenerUsuarioActual(): UsuarioActivo | null {
  const u: UsuarioSesion | null = modoDev ? usuarioDev : obtenerUsuarioGuardado();
  if (!u) return null;
  return {
    name: u.nombre,
    username: u.usuario,
    iniciales: obtenerIniciales(u.nombre),
    rol: u.rol,
  };
}

/** True si el usuario activo tiene rol 'admin'. */
export function esAdmin(): boolean {
  return obtenerUsuarioActual()?.rol === 'admin';
}

/**
 * True si el usuario puede GESTIONAR la operación: admin o supervisor.
 * Habilita cierre, mover/avanzar sprint, reordenar, habilitar obras y mano de obra.
 */
export function esGestion(): boolean {
  const r = obtenerUsuarioActual()?.rol;
  return r === 'admin' || r === 'supervisor';
}

/**
 * True si el usuario es 'visualizador': mismo alcance de pantallas que
 * 'campo' pero sin poder capturar avance ni registrar NC (solo lectura).
 */
export function esSoloLectura(): boolean {
  return obtenerUsuarioActual()?.rol === 'visualizador';
}
